import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { TimePicker, DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormControl, InputLabel, MenuItem, Select, TextField, Breadcrumbs, Checkbox } from "@mui/material";
import { MdDelete, MdAdd, MdUpdate } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";
import BaseUrl from "../../../Api/baseurl";
import FormAddDialog from "../../../Components/Slot-Add Modals/formadd";

const SlotSettings = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [slotsData, setSlotsData] = useState({
    Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
    Friday: [], Saturday: [], Sunday: []
  });
  const [errors, setErrors] = useState({});
  const [basePath, setBasePath] = useState(null);
  const [generationSettings, setGenerationSettings] = useState({
    number_of_days: 0,
    check_days: false,
    until_date: dayjs().add(1, "month"),
    auto_generate: false,
    last_generated_at: null
  });

  useEffect(() => {
    const isSuperuser = Cookies.get('is_superuser') === 'true';
    const isStaff = Cookies.get('is_staff') === 'true';
    const isVendor = Cookies.get('is_vendor') === 'true';
    if (isSuperuser) {
      setBasePath("/admin");
    } else if (isStaff && !isSuperuser) {
      setBasePath("/staff");
    } else if (isVendor) {
      setBasePath("/vendor");
    }
    getStaffData();
  }, []);

  useEffect(() => {
    if (selectedStaff) getSlotData(); getSlotGenerationSettings();
  }, [selectedStaff]);

  const getStaffData = async () => {
    const token = Cookies.get("token");
    try {
      setLoading(true);
      const response = await axios.get(`${BaseUrl}/staff/list/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaff(response.data.staff || []);
      // if (response.data.staff?.length > 0) {
      //   setSelectedStaff(response.data.staff[0].id);
      // }
    } catch (error) {
      handleError(error, "Failed to fetch staff");
    } finally {
      setLoading(false);
    }
  };

  const getSlotGenerationSettings = async () => {
    const token = Cookies.get("token");
    try {
      const response = await axios.get(`${BaseUrl}/staff/generation-settings/${selectedStaff}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { number_of_days, check_days, until_date, auto_generate, last_generated_at } = response.data;
      setGenerationSettings({
        number_of_days,
        check_days,
        until_date: until_date ? dayjs(until_date) : null,
        auto_generate,
        last_generated_at: last_generated_at ? dayjs(last_generated_at) : null
      });
    } catch (error) {
      if (error.response?.status === 404) {
        setGenerationSettings({
          number_of_days: 7,
          check_days: true,
          until_date: dayjs().add(1, "month"),
          auto_generate: false,
          last_generated_at: null
        });
      } else {
        handleError(error, "Failed to fetch generation settings");
      }
    }
  };

  const getSlotData = async () => {
    const token = Cookies.get("token");
    try {
      const response = await axios.get(`${BaseUrl}/staff/slot-settings/${selectedStaff}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { slot_settings_list } = response.data;
      const transformedSlots = {
        Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
        Friday: [], Saturday: [], Sunday: []
      };
      slot_settings_list.forEach(slot => {
        transformedSlots[slot.day_of_week].push({
          id: slot.id,
          start_time: slot.start_time,
          end_time: slot.end_time,
          no_of_slots: slot.no_of_slots,
          time_block: slot.time_block
        });
      });
      setSlotsData(transformedSlots);
    } catch (error) {
      console.error("Error fetching slot data", error);
    }
  };

  const handleError = (error, defaultMessage) => {
    if (error.response?.status === 401) {
      Swal.fire({ icon: "warning", title: "Session expired. Please login again." });
      Cookies.remove("token");
      Cookies.remove("username");
      Cookies.remove("is_superuser");
      Cookies.remove("is_staff");
      Cookies.remove("is_vendor");
      Cookies.remove("status");
      Cookies.remove("roles");
      Cookies.remove("subroles");
      navigate(`${basePath}/login`);
    } else {
      Swal.fire({ title: "Error", text: defaultMessage, icon: "error" });
    }
  };

  const validateTimeSlot = (day, index) => {
    const slot = slotsData[day][index];
    const errors = {};
    if (!slot.start_time || !slot.end_time) {
      errors.time = "Both times required";
    } else if (dayjs(slot.start_time, "HH:mm:ss").isAfter(dayjs(slot.end_time, "HH:mm:ss"))) {
      errors.time = "End must be after start";
    }
    return errors;
  };

  const handleAddSlot = (day) => {
    const newSlot = {
      id: Date.now(),
      start_time: "09:00:00",
      end_time: "10:00:00",
      no_of_slots: 1,
      time_block: "morning"
    };
    setSlotsData(prev => ({
      ...prev,
      [day]: [...prev[day], newSlot]
    }));
  };

  const handleUpdateSlot = async (day) => {
    const token = Cookies.get("token");
    const validationErrors = slotsData[day].map((_, index) => validateTimeSlot(day, index));

    if (validationErrors.some(err => Object.keys(err).length > 0)) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix validation errors before updating"
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to update the slots for ${day}. Do you want to continue?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, update it!',
      cancelButtonText: 'No, cancel'
    });

    if (result.isConfirmed) {
      try {
        const slotsToUpdate = slotsData[day].map(slot => ({
          id: slot.id,
          time_block: slot.time_block,
          start_time: slot.start_time,
          end_time: slot.end_time,
          no_of_slots: slot.no_of_slots
        }));

        const response = await axios.put(
          `${BaseUrl}/staff/slot-generation/${selectedStaff}/${day}/`,
          slotsToUpdate,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setSlotsData(prev => ({
          ...prev,
          [day]: response.data.updated
        }));

        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Slots for ${day} updated successfully!`
        });
      } catch (error) {
        handleError(error, `Failed to update slots for ${day}`);
      }
    }
  };

  const handleTimeChange = (day, index, type, newValue) => {
    const updatedSlots = [...slotsData[day]];
    updatedSlots[index] = {
      ...updatedSlots[index],
      [type === "start" ? "start_time" : "end_time"]: newValue ? newValue.format("HH:mm:ss") : null
    };
    setSlotsData(prev => ({ ...prev, [day]: updatedSlots }));
    setErrors(validateTimeSlot(day, index));
  };

  const handleSlotsChange = (day, index, value) => {
    const updatedSlots = [...slotsData[day]];
    updatedSlots[index].no_of_slots = value;
    setSlotsData(prev => ({ ...prev, [day]: updatedSlots }));
  };

  const handleTimeBlockChange = (day, index, value) => {
    const updatedSlots = [...slotsData[day]];
    updatedSlots[index].time_block = value;
    setSlotsData(prev => ({ ...prev, [day]: updatedSlots }));
  };

  const handleSubSlotDelete = async (day, slotId) => {
    const token = Cookies.get("token");
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it'
      });

      if (result.isConfirmed) {
        await axios.delete(
          `${BaseUrl}/staff/slot-generation/${selectedStaff}/${day}/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: { slot_ids: [slotId] }
          }
        );

        setSlotsData(prev => ({
          ...prev,
          [day]: prev[day].filter(slot => slot.id !== slotId)
        }));

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Slot deleted successfully!"
        });
      }
    } catch (error) {
      handleError(error, "Failed to delete slot");
    }
  };

  const handleDayDelete = async (day) => {
    const token = Cookies.get("token");

    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Delete all slots for ${day}? You won't be able to revert this!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete them!',
        cancelButtonText: 'No, keep them'
      });

      if (result.isConfirmed) {
        const slotIds = slotsData[day].map(slot => slot.id);

        if (slotIds.length > 0) {
          const response = await axios.delete(
            `${BaseUrl}/staff/slot-generation/${selectedStaff}/${day}/`,
            {
              headers: { Authorization: `Bearer ${token}` },
              data: { slot_ids: slotIds }
            }
          );

          setSlotsData(prev => ({ ...prev, [day]: [] }));

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: `Successfully deleted ${response.data.deleted_count} slots for ${day}`
          });
        } else {
          Swal.fire({
            icon: "info",
            title: "Nothing to delete",
            text: `No slots exist for ${day}`
          });
        }
      }
    } catch (error) {
      handleError(error, `Failed to delete slots for ${day}`);
    }
  };

  const handleSaveSettings = async () => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to save the settings. Do you want to continue?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, save it!',
      cancelButtonText: 'No, cancel'
    });

    // If the user confirms, proceed with saving the settings
    if (result.isConfirmed) {
      const token = Cookies.get("token");
      const formattedSettings = {
        ...generationSettings,
        until_date: generationSettings.until_date ? generationSettings.until_date.format("YYYY-MM-DD") : null
      }
      try {
        await axios.put(
          `${BaseUrl}/staff/generation-settings/${selectedStaff}/`,
          formattedSettings,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Settings saved successfully!"
        });
      } catch (error) {
        handleError(error, "Failed to save settings");
      }
    }
  };


  const handleReset = () => {
    setGenerationSettings({
      number_of_days: 0,
      check_days: false,
      until_date: dayjs().add(1, "month"),
      auto_generate: false,
      last_generated_at: null
    });
  };

  const getUsedTimeBlocks = (day) => {
    return slotsData[day].map(slot => slot.time_block);
  };

  return (
    <div className="py-4 px-4 md:py-6 md:px-6 w-full bg-gray-50 min-h-screen">
      <div className="ml-1 mb-3">
        <Breadcrumbs separator="›" aria-label="breadcrumb" className="text-sm text-blue-600">
          <Link className="hover:underline" color="inherit" to={`${basePath}/`}>
            Dashboard
          </Link>
          <Link className="hover:underline text-inherit" color="inherit" to={`${basePath}/manageslots`}>
            Manage Slots
          </Link>
          <Link className="hover:underline text-inherit" color="inherit" to="#">
            Slot Settings
          </Link>
        </Breadcrumbs>
      </div>

      <div className="w-full bg-white rounded-lg shadow-sm p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Slot Settings</h1>
        </div>

        <main className="flex flex-col space-y-6">
          <div className="mb-4">
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-1">
              Staff <span className="text-red-500">*</span>
            </label>
            {loading ? (
              <p className="text-sm text-gray-500">Loading staff...</p>
            ) : staff.length > 0 ? (
              <select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                className="block w-full md:w-1/2 h-10 bg-white rounded-md border border-gray-300 pl-3 pr-8 py-1 text-sm md:text-base text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Staff</option>
                {staff.map((staf) => (
                  <option key={staf.id} value={staf.id}>
                    {staf.first_name} {staf.last_name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-red-500">No staff available</p>
            )}
          </div>

          {selectedStaff && Object.entries(slotsData).map(([day, slots]) => (
            <div key={day} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800 capitalize">{day}</h2>
                <div className="flex gap-2">
                  <FormAddDialog
                    dateName={day}
                    staffId={selectedStaff}
                    onSlotAdded={getSlotData}
                  />
                  <button
                    onClick={() => handleUpdateSlot(day)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md flex items-center gap-1 transition-colors"
                  >
                    <MdUpdate className="text-base" /> Update
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {slots.length > 0 ? (
                  slots.map((slot, index) => (
                    <div key={slot.id} className="border border-gray-200 rounded-md p-3 bg-white">
                      <div className="flex justify-between items-center mb-4">
                        <FormControl size="small" className="">
                          <InputLabel>Time Block</InputLabel>
                          <Select
                            value={slot.time_block}
                            label="Time Block"
                            onChange={(e) => handleTimeBlockChange(day, index, e.target.value)}
                          >
                            <MenuItem value="morning" disabled={getUsedTimeBlocks(day).includes("morning") && slot.time_block !== "morning"}>
                              Morning
                            </MenuItem>
                            <MenuItem value="afternoon" disabled={getUsedTimeBlocks(day).includes("afternoon") && slot.time_block !== "afternoon"}>
                              Afternoon
                            </MenuItem>
                            <MenuItem value="evening" disabled={getUsedTimeBlocks(day).includes("evening") && slot.time_block !== "evening"}>
                              Evening
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <button
                          onClick={() => handleSubSlotDelete(day, slot.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            label="Start"
                            value={dayjs(slot.start_time, "HH:mm:ss")}
                            ampm={false}
                            onChange={(newValue) => handleTimeChange(day, index, "start", newValue)}
                            slotProps={{
                              textField: {
                                size: "small",
                                error: !!errors.time,
                                helperText: errors.time,
                              },
                            }}
                          />
                          <TimePicker
                            label="End"
                            value={dayjs(slot.end_time, "HH:mm:ss")}
                            ampm={false}
                            onChange={(newValue) => handleTimeChange(day, index, "end", newValue)}
                            slotProps={{ textField: { size: "small" } }}
                          />
                        </LocalizationProvider>

                        <TextField
                          size="small"
                          label="No. of Slots"
                          type="number"
                          value={slot.no_of_slots}
                          onChange={(e) => handleSlotsChange(day, index, parseInt(e.target.value))}
                          className=""
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full p-4 text-center">
                    <p className="text-gray-500 text-sm md:text-base">No slots available</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-3">
                <button
                  onClick={() => handleDayDelete(day)}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center gap-1 transition-colors"
                >
                  <MdDelete className="text-base" /> Clear All
                </button>
              </div>
            </div>
          ))}

          {selectedStaff && (
            <section className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Generation Settings</h2>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 bg-white p-2 rounded-md border border-gray-200">
                  <Checkbox
                    checked={generationSettings.check_days}
                    onChange={(e) => setGenerationSettings(prev => ({ ...prev, check_days: e.target.checked }))}
                    color="primary"
                    size="small"
                  />
                  <span className="text-sm text-gray-700">Generate for next</span>
                  <FormControl size="small" variant="outlined">
                    <Select
                      value={generationSettings.number_of_days}
                      onChange={(e) => setGenerationSettings(prev => ({ ...prev, number_of_days: e.target.value }))}
                      sx={{ width: 70 }}
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <span className="text-sm text-gray-700">days</span>
                </div>

                <div className="flex items-center gap-2 bg-white p-2 rounded-md border border-gray-200">
                  <Checkbox
                    checked={generationSettings.auto_generate}
                    onChange={(e) => setGenerationSettings(prev => ({ ...prev, auto_generate: e.target.checked }))}
                    color="primary"
                    size="small"
                  />
                  <span className="text-sm text-gray-700">Auto-generate</span>
                </div>

                <div className="bg-white p-2 rounded-md border border-gray-200">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Valid until"
                      value={generationSettings.until_date}
                      onChange={(newValue) => setGenerationSettings(prev => ({ ...prev, until_date: newValue }))}
                      slotProps={{ textField: { size: "small" } }}
                      sx={{ width: 150 }}
                    />
                  </LocalizationProvider>
                </div>
              </div>
            </section>
          )}

          {selectedStaff && (
            <footer className="flex justify-start gap-3 mt-4">
              <button
                onClick={handleSaveSettings}
                className="rounded-md bg-[#BF9455] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#eeae4f]"
              >
                Save
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-md transition-colors"
              >
                Reset
              </button>
              <Link
                to={`${basePath}/manageslots`}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 text-sm rounded-md transition-colors"
              >
                Cancel
              </Link>
            </footer>
          )}
        </main>
      </div>
    </div>
  );
};

export default SlotSettings;